import React, { useRef } from 'react';
import '../billing/styles/BillingDashboard.scss';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend, BarElement
} from 'chart.js';
import {
  getElementAtEvent, Line, Bar, Pie
} from 'react-chartjs-2';
import PropTypes from 'prop-types';
import { formatCurrency } from '../../utils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  ArcElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// matching variables.scss
const blues = [
  '#01497F',
  // '#145DA9', Hard to distinguish from the adjacent colors
  '#218CDB',
  '#8FD3F9',
  '#CDECF9',
  '#E0F9FC',
  '#337ab7',
  '#A7D5F1',
  '#197CB7',
  '#63D7D9',
  '#105C89'
];

const pointStyles = [
  'circle',
  'rect',
  'triangle'
];

const getColor = (index) => blues[index % blues.length];

const LineGraph = ({
  xLabels, datasets, onClick, title, formatYLabels, buildTooltip, className
}) => {
  const data = {
    labels: xLabels,
    datasets: datasets.map((dataset, index) => ({
      label: dataset.label,
      data: dataset.points.map((value) => value.value),
      borderColor: getColor(index),
      backgroundColor: getColor(index),
      pointStyle: pointStyles[index],
      pointRadius: 8
    }))
  };

  const options = {
    maintainAspectRatio: false,
    interaction: {
      intersect: false
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxHeight: 10,
          usePointStyle: true
        }
      },
      tooltip: {
        callbacks: {}
      }
    },
    scales: {
      y: {
        min: 0,
        ticks: {}
      }
    }
  };

  if (buildTooltip) {
    options.plugins.tooltip.callbacks.label = buildTooltip;
  }

  if (formatYLabels) {
    options.scales.y.ticks.callback = formatYLabels;
  }

  if (title) {
    options.plugins.title = {
      display: true,
      text: title
    };
  }

  const chartRef = useRef(null);

  const handleClickEvent = (event) => {
    const { current: chart } = chartRef;
    if (!chart) {
      return;
    }

    onClick(getElementAtEvent(chart, event));
  };

  return <Line ref={chartRef} options={options} data={data} onClick={handleClickEvent} className={className} />;
};
LineGraph.propTypes = {
  xLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
  datasets: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    points: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.number
    }))
  })).isRequired,
  /* takes dataset element that was clicked on and performs on action */
  onClick: PropTypes.func,
  title: PropTypes.string,
  /* takes value, index, and ticks and returns formatted string */
  formatYLabels: PropTypes.func,
  /* builds tooltip string based on context */
  buildTooltip: PropTypes.func,
  className: PropTypes.string
};
LineGraph.defaultProps = {
  onClick: undefined,
  title: undefined,
  formatYLabels: undefined,
  buildTooltip: undefined,
  className: ''
};

const BarGraph = ({
  xLabels, datasets, title, buildTooltip, tooltipSort, formatYLabels, className
}) => {
  const options = {
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index'
    },
    plugins: {
      legend: {
        position: 'bottom'
      },
      title: {
        display: true,
        text: title
      },
      tooltip: {
        callbacks: {}
      }
    },
    scales: {
      x: { stacked: false },
      y: {
        stacked: false,
        ticks: {},
        min: 0
      }
    }
  };

  if (buildTooltip) {
    options.plugins.tooltip.callbacks.label = buildTooltip;
  }

  if (tooltipSort) {
    options.plugins.tooltip.itemSort = tooltipSort;
  }

  if (formatYLabels) {
    options.scales.y.ticks.callback = formatYLabels;
  }

  const data = {
    labels: xLabels,
    datasets: datasets.map((dataset, i) => ({
      label: dataset.label,
      data: dataset.bars,
      backgroundColor: getColor(datasets.length - 1 - i)
    }))
  };

  return (
    <Bar options={options} data={data} className={className} />
  );
};
BarGraph.propTypes = {
  xLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
  datasets: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    bars: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string]))
  })).isRequired,
  title: PropTypes.string,
  /* builds tooltip string based on context */
  buildTooltip: PropTypes.func,
  /* sort function for tooltips */
  tooltipSort: PropTypes.func,
  /* takes value, index, and ticks and returns formatted string */
  formatYLabels: PropTypes.func,
  className: PropTypes.string
};
BarGraph.defaultProps = {
  tooltipSort: undefined,
  title: undefined,
  formatYLabels: undefined,
  buildTooltip: undefined,
  className: ''
};

const PieChart = ({
  data, title, className, buildTooltip
}) => {
  const options = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'left'
      },
      title: {
        display: true,
        text: title
      },
      tooltip: {
        callbacks: {}
      }
    }
  };

  if (buildTooltip) {
    options.plugins.tooltip.callbacks.label = buildTooltip;
  }

  const labels = [];
  const values = [];
  const colors = [];

  data.forEach((labelValue, index) => {
    labels.push(labelValue.label);
    values.push(labelValue.value);
    colors.push(getColor(index));
  });

  const formattedData = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: colors
      }
    ]
  };

  return (
    <Pie data={formattedData} options={options} className={className} />
  );
};
PieChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  })).isRequired,
  title: PropTypes.string,
  /* builds tooltip string based on context */
  buildTooltip: PropTypes.func,
  className: PropTypes.string
};
PieChart.defaultProps = {
  title: undefined,
  buildTooltip: undefined,
  className: ''
};

const formatTooltipCurrency = (label, value) => {
  let tooltip = label || '';
  if (tooltip) {
    tooltip += ': ';
  }
  if (tooltip !== null) {
    tooltip += formatCurrency(value).split('.')[0];
  }
  return tooltip;
};

const formatYLabelsPercent = (value, index, ticks) => `${value}%`;

const formatYLabelsCurrency = (value, index, ticks) => formatCurrency(value).split('.')[0];

export {
  LineGraph,
  BarGraph,
  PieChart,
  formatTooltipCurrency,
  formatYLabelsCurrency,
  formatYLabelsPercent
};
